import {
  AddressingType,
  BuildingCreateDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
  FloorConfigDTO,
  RFAConfigDTO,
  RiserConfigDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { 
  DEFAULT_NUMBER_OF_FLOORS_IN_RISER, 
  DEFAULT_NUMBER_OF_RISERS_IN_BUILDING, 
  distributeApartmentsToRaisersAndFloors 
} from '@/core/utils/EntrancePlant/entrance-plant.utils';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';

@Options({
  name: 'DialogAddEditBuilding',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    buildingToEdit: {
      type: Object as PropType<BuildingDTO>,
      required: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.validApartmentNumber = true;
        this.invalidNumberReason = '';
        this.brfaStep = 0;
        this.rfaConfig.risers = [];
        this.initBuilding();
      }
    },
    "building.apartmentsCount": function () {
      this.checkValidApartmentNumber();
    },
    'building.name': function () {
      if (this.building) {
        this.verifyValidityOfName();
      }
    },
  },
  components: { NumberButtons },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditBuilding extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisible!: boolean;
  buildingToEdit!: BuildingDTO;
  isDialogVisible = false;

  isEdit = false;
  building!: BuildingCreateDTO;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  isBrfa = false;
  brfaStep = 0;
  newRisersNumber = 1;
  rfaConfig: RFAConfigDTO = new RFAConfigDTO();
  apartmentCountValues: { label: number, value: number }[] = [];
  validApartmentNumber = true;
  invalidNumberReason = '';

  mounted() {
    this.isDialogVisible = this.isVisible;
    this.isBrfa =
      this.currentEntrancePlant.addressingType ===
      AddressingType.BuildingRiserFloorApartment;
  }

  async initBuilding() {
    this.building = new BuildingCreateDTO();
    if (this.buildingToEdit) {
      this.isEdit = true;
      this.building.buildingId = this.buildingToEdit.buildingId;
      this.building.apartmentsCount = this.buildingToEdit.apartments?.length;
      this.building.name = this.buildingToEdit.name;
    } else {
      this.isEdit = false;
      let i = 0;
      do {
        i++;
        const newName = `${this.$t('AppResourceEP.Building')} ${
          this.currentEntrancePlant.buildings!.length + i
        }`;
        this.building.name = newName;
        this.verifyValidityOfName();
      } while (!this.isValidName);
      this.building.apartmentsCount = 1;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    if (this.isEdit) return this.$t('AppResourceEP.RenameBuilding');
    switch (this.brfaStep) {
      case 0:
        return this.$t('AppResourceEP.AddNewBuilding');
      case 1:
        return this.$t('AppResourceEP.SetupWizardStepBuildingRisers');
      case 2:
        return this.$t('AppResourceEP.SetupWizardStepBuldingFloors');
      case 3:
        return this.$t('AppResourceEP.SetupWizardStepBuidingLinkApartemntsTitle');
    }
  }

  get inputLabel() {
    return `${this.$t('AppResource.NameUpper')} ${this.$t(
      'AppResourceEP.Building'
    )}`;
  }

  get buttonSubmitLabel() {
    if (!this.isBrfa || this.isEdit) return this.$t('AppResource.Save');

    if (this.brfaStep < 3) {
      return this.$t('AppResource.Continue');
    } else {
      return this.$t('AppResource.Save');
    }
  }

  get secondaryButtonLabel() {
    if (this.brfaStep > 0) {
      return this.$t('AppResource.Back');
    } else {
      return this.$t('AppResource.Cancel');
    }
  }

  get saveButtonDisabled() {
    const disabledForNameCheck =
      !this.isValidName || !this.building?.name?.trim();
    const disabledForApartments =
      !this.building?.apartmentsCount || !this.validApartmentNumber;
    if (this.isBrfa && this.brfaStep === 3) {
      return disabledForNameCheck || disabledForApartments || !this.allApartmentsAssigned();
    }
    return disabledForNameCheck || disabledForApartments;
  }

  get availableApartments() {
    const totalPlantApartments = this.currentEntrancePlant.buildings?.reduce(
      (acc, building) => acc + building.apartments?.length!,
      0
    ) || 0;
    return 3999 - totalPlantApartments;
  }

  get risersCount() {
    if (!this.rfaConfig || !this.rfaConfig.risers) return 0;
    return this.rfaConfig.risers.length;
  }

  get totalAssignedApartments() {
    if (!this.rfaConfig || !this.rfaConfig.risers) return 0;
    return this.rfaConfig.risers.reduce(
      (acc, riser) =>
        acc +
        riser.floors!.reduce(
          (acc, floor) => acc + (floor.numberOfApartments ?? 0),
          0
        ),
      0
    );
  }

  get mismatchedApartmentsErrorText() {
    const firstLine = this.$t('entrancePlant.buildingApartments') + ': ' + this.rfaConfig.numberOfApartments;
    const secondLine = this.$t('entrancePlant.assignedApartmets') + ': ' + this.totalAssignedApartments;
    return [firstLine, secondLine];
  }

  updateApartmentCountValues() {
    if (this.isBrfa && this.building.apartmentsCount)
      this.apartmentCountValues = [
        ...Array(+this.building.apartmentsCount + 1).keys(),
      ].map((i) => { return { label: i, value: i }; });
  }

  riserName(index: number) {
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Riser'
    )} ${toLabel(index, this.currentEntrancePlant.denominationRisers)}`;
  }

  floorsCount(riser: RiserConfigDTO) {
    if (!riser.floors) return 0;
    return riser.floors.length;
  }

  floorName(index: number) {
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Floor'
    )} ${toLabel(index, this.currentEntrancePlant.denominationFloors)}`;
  }

  floorLabel(floor: FloorConfigDTO, index: number) {
    let label = `${this.$t('AppResourceEP.Floor')} ${toLabel(
      index,
      this.currentEntrancePlant.denominationFloors
    )}`;

    if (floor.name !== label) {
      label += ` - ${floor.name}`;
    }

    return label;
  }

  addRiser(risersToAdd = 1) {
    if (!this.rfaConfig) return;
    if (!this.rfaConfig.risers) this.rfaConfig.risers = [];

    for (let index = 0; index < risersToAdd; index++) {
      const riserIndex = this.rfaConfig.risers.length + 1;
      const riserName = `${this.$t('AppResourceEP.Riser')} ${toLabel(
        riserIndex,
        this.currentEntrancePlant.denominationRisers
      )}`;

      const riser = new RiserConfigDTO({
        index: riserIndex,
        name: riserName,
      });

      this.addFloor(riser, DEFAULT_NUMBER_OF_FLOORS_IN_RISER);
      this.rfaConfig.risers.push(riser);
    }
    distributeApartmentsToRaisersAndFloors(this.rfaConfig);
  }

  removeRiser() {
    if (!this.rfaConfig || !this.rfaConfig.risers) return;
    this.rfaConfig.risers.splice(this.rfaConfig.risers.length - 1, 1);
    distributeApartmentsToRaisersAndFloors(this.rfaConfig);
  }

  isLast(riserIndex: number) {
    if (!this.rfaConfig || !this.rfaConfig.risers) return false;
    return riserIndex + 1 === this.rfaConfig.risers.length;
  }

  addFloor(riser: RiserConfigDTO, floorsCount = 1) {
    if (!riser.floors) riser.floors = [];

    for (let index = 0; index < floorsCount; index++) {
      const floorIndex = riser.floors.length + 1;
      const floorName = `${this.$t('AppResourceEP.Floor')} ${toLabel(
        floorIndex,
        this.currentEntrancePlant.denominationFloors
      )}`;

      riser.floors.push(
        new FloorConfigDTO({
          index: floorIndex,
          name: floorName,
        })
      );
    }

    if (!this.rfaConfig || this.rfaConfig.risers?.length === 0) return;
    distributeApartmentsToRaisersAndFloors(this.rfaConfig);
  }

  removeFloor(riser: RiserConfigDTO) {
    if (!riser.floors) return;
    riser.floors.pop();
    distributeApartmentsToRaisersAndFloors(this.rfaConfig);
  }

  verifyValidityOfName() {
    if (this.building?.name === undefined) return;

    const nameToCheck = this.building.name.trim();
    let listToCheck = this.currentEntrancePlant.buildings || [];

    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (editing) => editing.buildingId !== this.buildingToEdit.buildingId
      );
    }

    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() == nameToCheck.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  checkValidApartmentNumber() {
    this.validApartmentNumber = +this.building.apartmentsCount! <= this.availableApartments && +this.building.apartmentsCount! > 0;
    this.invalidNumberReason = this.validApartmentNumber ? '' : this.$t('entrancePlant.apartmentLimitReached', { maxApartments: 3999 });
    this.updateApartmentCountValues();
  }
  
  allApartmentsAssigned() {
    if (!this.rfaConfig || !this.rfaConfig.risers) return false;
    return this.rfaConfig.numberOfApartments === this.totalAssignedApartments;
  }

  handleButtonSubmitAction() {
    if (!this.isBrfa || this.isEdit) return this.lastStepFunction();

    switch (this.brfaStep) {
      case 0:
        this.rfaConfig.numberOfApartments = +(this.building.apartmentsCount || 0);
        this.addRiser(DEFAULT_NUMBER_OF_RISERS_IN_BUILDING);
        this.brfaStep++;
        break;

      case 1:
        this.brfaStep++;
        break;
      
      case 2:
        this.updateApartmentCountValues();
        this.brfaStep++;
        break;

      case 3:
        this.lastStepFunction();
        break;
    }
  }

  handleSecondaryButtonAction() {
    switch (this.brfaStep) {
      case 0:
        this.handleHideDialog();
        break;

      case 1:
        this.rfaConfig.risers = [];
        this.brfaStep--;
        break;

      default:
        this.brfaStep--;
        break;
    }
  }

  async lastStepFunction() {
    this.isDialogVisible = false;
    this.$store.dispatch(SET_APP_LOADING, true);

    await this.saveBuilding();

    this.$store.dispatch(SET_APP_LOADING, false);

    this.$emit('update-list');
  }

  retrieveEditedStructure() {
    const structure = new EntrancePlantStructureCreateDTO();
    structure.buildingsCreate = [];
    this.currentEntrancePlant.buildings?.forEach((epBuilding) =>
      structure.buildingsCreate?.push(
        new BuildingCreateDTO({
          buildingId: epBuilding.buildingId,
          name: epBuilding.name,
          apartmentsCount: epBuilding.apartments?.length,
        })
      )
    );

    if (this.isEdit) {
      const editingIndex = structure.buildingsCreate.findIndex(
        (editing) => editing.buildingId === this.building.buildingId
      );
      structure.buildingsCreate.splice(editingIndex, 1, this.building);
    } else {
      structure.buildingsCreate.push(this.building);
    }
    return structure;
  }

  async saveBuilding() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    try {
      const editStructure = this.retrieveEditedStructure();
      await plantsClient.structure(
        this.currentEntrancePlant.desktopId,
        editStructure
      );
      if (this.isBrfa && !this.isEdit) {
        const entrPlant = await plantsClient.entranceplantGET(
          this.currentEntrancePlant.desktopId!,
          this.currentEntrancePlant.lastModify
        );
        const rfaStructure: { [key: string]: RFAConfigDTO } = {};
        const newBuild = entrPlant.buildings?.find(
          (build) => build.name === this.building.name
        );
        rfaStructure[newBuild!.buildingId!] = this.rfaConfig;

        const structure = new EntrancePlantStructureCreateDTO({
          plantConfigStructure: rfaStructure,
        });

        await plantsClient.structure(
          this.currentEntrancePlant.desktopId!,
          structure
        );
      }
      
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    }
  }
}
